import clsx from 'clsx';
import { AlertTriangle, Info, Moon, OctagonAlert } from 'lucide-react';
import { useMemo, useState } from 'react';
import { groupWarnings, shiftTimeParts, type ClockCompact, type Shift, type WarningGroup, type WtrWarning } from '@tradeoutlet/shared';
import { PersonButton } from '@/features/people/PersonDrawer';
import { getClockPref } from '@/lib/clockPref';
import { useClockPref } from '@/lib/clock';
import { dt } from '@/lib/format';
import { useLocations, useMembers, usePositions } from '@/lib/hooks';

/** "09:00–17:00", overnight "22:00–06:00 Thu" (never "(+1)"), in the person's 12 / 24-hour clock. */
export function shiftTimes(s: Pick<Shift, 'starts_at' | 'ends_at'>, zone?: string) {
  return shiftTimeParts(dt(s.starts_at, zone), dt(s.ends_at, zone), getClockPref()).text;
}

/** Narrow chips (below ~1440px the day columns shrink): "6–14", "6:30–14:15", "22–6 Thu"; 12-hour "6AM–2PM" (tight; 7 characters like "22–6 Thu"). Full form in the label/title. */
export function shortShiftTimes(s: Pick<Shift, 'starts_at' | 'ends_at'>, zone?: string) {
  return shiftTimeParts(dt(s.starts_at, zone), dt(s.ends_at, zone), getClockPref(), 'tight').text;
}

/**
 * Shift times for the screen: the range, and for overnight shifts the end day with a small moon whose tooltip and
 * label say "Ends Thu 15 Oct at 06:00".
 */
export function ShiftTimesLabel({ s, zone, compact, className }: { s: Pick<Shift, 'starts_at' | 'ends_at'>; zone?: string; compact?: ClockCompact; className?: string }) {
  const p = shiftTimeParts(dt(s.starts_at, zone), dt(s.ends_at, zone), useClockPref(), compact);
  if (!p.endDay) return <span className={className}>{p.range}</span>;
  return (
    // Caller's className (often responsive show/hide) goes on the outer span alone, so our own display class can't override it.
    <span className={className} title={p.endsLabel ?? undefined} data-testid="overnight">
      <span className="inline-flex max-w-full items-center gap-1">
        <span aria-hidden className="truncate">{p.range} {p.endDay}</span>
        <Moon className="h-3 w-3 shrink-0" aria-hidden />
        <span className="sr-only">{p.range}, {p.endsLabel}</span>
      </span>
    </span>
  );
}

export function shiftPaidMinutes(s: Pick<Shift, 'starts_at' | 'ends_at' | 'break_minutes'>) {
  return Math.max(0, (new Date(s.ends_at).getTime() - new Date(s.starts_at).getTime()) / 60000 - s.break_minutes);
}

export function useWfLookups() {
  const locs = useLocations();
  const pos = usePositions();
  const locations = locs.data?.locations.filter(l => !l.archived_at) ?? [];
  const positions = pos.data?.positions.filter(p => !p.archived_at) ?? [];
  return {
    locations, positions,
    locationName: (id: string | null | undefined) => locations.find(l => l.id === id)?.name ?? '—',
    position: (id: string | null | undefined) => positions.find(p => p.id === id),
    locationTz: (id: string | null | undefined) => locations.find(l => l.id === id)?.timezone ?? 'Europe/London',
  };
}

/**
 * Working-time & availability checks (t166u): one entry per warning type with the people it affects underneath
 * (name opens their profile; per-person detail such as the day or expiry only where it differs). Errors first, then
 * by number of people. Used by every check list: schedule page, shift editor, publish/backlog, leave preview, timesheets.
 */
export function WarningList({ warnings, className, onOpenShift }: { warnings: WtrWarning[]; className?: string; onOpenShift?: (shiftId: string) => void }) {
  const { byId } = useMembers();
  const groups = useMemo(() => groupWarnings(warnings), [warnings]);
  if (!warnings.length) return null;
  return (
    <ul className={clsx('space-y-1', className)} data-testid="warning-groups">
      {groups.map(g => <WarningGroupRow key={g.key} g={g} nameOf={id => byId.get(id)?.display_name ?? 'Someone'} onOpenShift={onOpenShift} />)}
    </ul>
  );
}

const PEOPLE_SHOWN = 4;
function WarningGroupRow({ g, nameOf, onOpenShift }: { g: WarningGroup; nameOf: (id: string) => string; onOpenShift?: (shiftId: string) => void }) {
  const [open, setOpen] = useState(false);
  const many = g.people.length > PEOPLE_SHOWN;
  const shown = many && !open ? g.people.slice(0, PEOPLE_SHOWN - 1) : g.people;
  const named = g.people.some(p => p.user_id) || g.people.some(p => p.details.length);
  return (
    <li data-testid="warning-group" data-code={g.code} data-severity={g.severity} className={clsx('rounded-md px-2.5 py-1.5 text-xs',
      g.severity === 'error' ? 'bg-rose-50 text-rose-800 dark:bg-rose-950 dark:text-rose-200' : g.severity === 'warning' ? 'bg-orange-50 text-orange-800 dark:bg-orange-950 dark:text-orange-200' : 'bg-sky-50 text-sky-800 dark:bg-sky-950 dark:text-sky-200')}>
      <div className="flex items-start gap-2">
        {g.severity === 'error' ? <OctagonAlert className="mt-px h-3.5 w-3.5 shrink-0" /> : g.severity === 'warning' ? <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" /> : <Info className="mt-px h-3.5 w-3.5 shrink-0" />}
        <span className="font-medium" data-testid="warning-group-title">{g.title}</span>
        {g.people.length > 1 && <span className="opacity-75">· {g.people.length} people</span>}
      </div>
      {named && (
        <ul className="ml-5 mt-1 flex flex-wrap gap-1">
          {shown.map(p => (
            <li key={p.user_id ?? 'open'} className="inline-flex max-w-full items-center gap-1 rounded-full bg-white/70 py-0.5 pl-1 pr-2 text-slate-800 dark:bg-slate-900/60 dark:text-slate-100" data-testid="warning-person">
              {p.user_id ? <PersonButton userId={p.user_id} name={nameOf(p.user_id)} className="shrink-0 whitespace-nowrap rounded-full px-1 font-medium hover:underline">{nameOf(p.user_id)}</PersonButton> : <span className="shrink-0 whitespace-nowrap px-1 font-medium">Open shift</span>}
              {p.details.length > 0 && (onOpenShift && p.shift_ids?.length
                ? <button type="button" className="min-w-0 truncate text-slate-500 underline-offset-2 hover:underline dark:text-slate-400" title={p.details.join('\n')}
                  onClick={() => onOpenShift(p.shift_ids![0])} aria-label={`Open the shift: ${p.details.join(', ')}`} data-testid="warning-shift">{p.details.join(', ')}</button>
                : <span className="min-w-0 truncate text-slate-500 dark:text-slate-400" title={p.details.join('\n')}>{p.details.join(', ')}</span>)}
            </li>
          ))}
          {many && (
            <li><button type="button" className="rounded-full px-2 py-0.5 font-medium underline-offset-2 hover:underline" aria-expanded={open} onClick={() => setOpen(o => !o)} data-testid="warning-more">
              {open ? 'Show fewer' : `+${g.people.length - shown.length} more`}
            </button></li>
          )}
        </ul>
      )}
    </li>
  );
}

export { STATUS_TONE, statusLabel, statusTone } from '@/lib/status';
import { statusLabel, statusTone } from '@/lib/status';
import { unpill } from '@/components/ui';

/** `auto` (t210u): approved automatically on close - a quieter grey "Auto-approved" so it doesn't read as a manager's sign-off. */
export function StatusBadge({ status, auto }: { status: string; auto?: boolean }) {
  const soft = auto && status === 'approved';
  return <span className={clsx('inline-flex whitespace-nowrap text-[11px] font-semibold', soft ? 'text-slate-600 dark:text-slate-300' : unpill(statusTone(status)), status === 'cancelled' && 'line-through')}
    data-testid="status-badge" data-status={status} data-auto={soft || undefined}>{soft ? 'Auto-approved' : statusLabel(status)}</span>;
}
